import { validateMatchSetup } from './matchSetup.validate.js';
import { buildInitialGameState } from './setup/buildInitialGameState.js';
import { TURN_STAGES } from './turn/turnPhases.types.js';
import { advanceTurnStage } from './turn/turnLoop.js';
import { drawCards, playCard, endTurn } from './moves/coreMoves.impl.js';
import { applyEndOfTurnCleanup } from './moves/endOfTurnCleanup.logic.js';
import { replayOutOfTimeCards } from './moves/outOfTime.logic.js';
import { resolveHeroChoice } from './moves/heroChoice.resolve.js';
import { resolveKoHeroChoice, hasPendingKoHeroChoice } from './moves/koHeroChoice.resolve.js';
import { resolveScryKoChoice, hasPendingScryKoChoice } from './moves/scryKoChoice.resolve.js';
import { resolveMelterKoChoice, hasPendingMelterKoChoice } from './moves/melterKoChoice.resolve.js';
import { resolveRuthlessDictatorChoice, hasPendingRuthlessDictatorChoice } from './moves/ruthlessDictatorChoice.resolve.js';
import { resolveElectromagneticBubbleChoice, hasPendingElectromagneticBubbleChoice } from './moves/electromagneticBubbleChoice.resolve.js';
import { resolveDiscardChoice, hasPendingDiscardChoice } from './moves/discardChoice.resolve.js';
import { resolvePutCardsOnDeckChoice, hasPendingPutCardsOnDeckChoice } from './moves/putCardsOnDeckChoice.resolve.js';
import { resolveReorderChoice, hasPendingReorderChoice } from './moves/reorderChoice.resolve.js';
import { resolveDefeatChoice, hasPendingDefeatChoice } from './moves/defeatChoice.resolve.js';
import { resolveKoDiscardChoice, hasPendingKoDiscardChoice } from './moves/koDiscardChoice.resolve.js';
import { resolveOptionalKoReward, hasPendingOptionalKoReward } from './moves/optionalKoReward.resolve.js';
import { resolveSmashDiscard, hasPendingSmashDiscard } from './moves/smashDiscard.resolve.js';
import { resolvePutHandOnDeckTop, hasPendingPutHandOnDeckTop } from './moves/putHandOnDeckTop.resolve.js';
import { resolveRevealTopDispose, hasPendingRevealTopDispose } from './moves/revealTopDispose.resolve.js';
import { resolveRevealThreeAssign, hasPendingRevealThreeAssign } from './moves/revealThreeAssign.resolve.js';
import { resolveDoOver, hasPendingDoOver } from './moves/doOver.resolve.js';
import { hasPendingPlayVillainTopChoice, resolvePlayVillainTopChoice } from './moves/playVillainTop.resolve.js';
import { resolveOptionalPutBottomHQ, hasPendingOptionalPutBottomHQ } from './moves/resolveOptionalPutBottomHQ.js';
import { resolvePutAnyNumberBottomHQ, hasPendingPutAnyNumberBottomHQ } from './moves/resolvePutAnyNumberBottomHQ.js';
import { resolveReturnZeroCostDiscard, hasPendingReturnZeroCostDiscard } from './moves/resolveReturnZeroCostDiscard.js';
import { resolveDiscardToPlay, hasPendingDiscardToPlay } from './moves/resolveDiscardToPlay.js';
import { resolveReturnOnDiscard, hasPendingReturnOnDiscard, openNonActiveReturnOnDiscardSeatChoiceIfNeeded } from './moves/resolveReturnOnDiscard.js';
import { resolveGiveHqHeroChoice, hasPendingGiveHqHeroChoice } from './moves/giveHqHeroChoice.resolve.js';
import { resolveCopyPowersChoice, hasPendingCopyPowersChoice } from './moves/copyPowersChoice.resolve.js';
import { resolveVictoryPileCardPick, hasPendingVictoryPileCardPick } from './moves/resolveVictoryPileCardPick.js';
import { resolveDrawOrEmpowered, hasPendingDrawOrEmpowered } from './moves/drawOrEmpowered.resolve.js';
import { resolveCoveringFireChoice, hasPendingCoveringFireChoice } from './moves/coveringFireChoice.resolve.js';
import { resolveMoveVillainChoice, hasPendingMoveVillainChoice } from './moves/moveVillainChoice.resolve.js';
import { resolveSplitFaceChoice, hasPendingSplitFaceChoice } from './moves/splitFaceChoice.resolve.js';
import { resolveCountScaledChoice, hasPendingCountScaledChoice } from './moves/countScaledChoice.resolve.js';
import { resolveUndercoverChoice, hasPendingUndercoverChoice } from './moves/undercover.resolve.js';
import { resolveSeatChoice, hasPendingSeatChoice, SEAT_CHOICE_STAGE } from './moves/seatChoice.resolve.js';
import { openDivingBlockSeatChoiceIfNeeded } from './moves/divingBlock.logic.js';
import { openFlyingShieldBlockSeatChoiceIfNeeded } from './moves/flyingShieldBlock.logic.js';
import { openOutOfTimeGrantSeatChoiceIfNeeded } from './moves/outOfTimeGrant.logic.js';
import { openEscapeProcedureSeatChoiceIfNeeded } from './villainDeck/villainEscapeProcedure.js';
import { openTimestreamPurgeSeatChoiceIfNeeded, sweepPurgedHeroesFromHq } from './rules/timestreamPurge.logic.js';
import { openEnigmaPeekSeatChoiceIfNeeded } from './rules/enigmaCode.logic.js';
import { openRevealOrWoundSeatChoiceIfNeeded } from './moves/revealOrWoundChoice.logic.js';
import { executeRuleHooks } from './rules/ruleRuntime.execute.js';
import { applyRuleEffects } from './rules/ruleRuntime.effects.js';
import { DEFAULT_IMPLEMENTATION_MAP } from './rules/ruleRuntime.impl.js';
import { evaluateEndgame } from './endgame/endgame.evaluate.js';
import { latchFinalTurnIfDeckExhausted, resolveFinalTurnTieIfUnresolved, } from './endgame/finalTurn.logic.js';
import { promoteMastermindVictoryIfPending } from './endgame/mastermindVictory.logic.js';
import { applyPileDepletionResourceLoss } from './rules/schemeResourceLoss.js';
import { resolveDeferredHeroGrants } from './hero/heroEffects.execute.js';
// why: WP-670 / D-24484 — Scheme Transform. Per-move check that flips a transforming scheme
// to its Great Old One face when the trigger (Chthon: 5 Bystanders in the KO pile) is met.
import { checkAndTransformScheme } from './scheme/schemeTransform.logic.js';
import { clearDeferredConditionalGrants } from './hero/deferredConditionalGrants.js';
import { setPlayerReady, startMatchIfReady } from './lobby/lobby.moves.js';
import { revealVillainCard } from './villainDeck/villainDeck.reveal.js';
import { fightVillain } from './moves/fightVillain.js';
import { recruitHero } from './moves/recruitHero.js';
import { recruitOfficer } from './moves/recruitOfficer.js';
import { recruitSidekick } from './moves/recruitSidekick.js';
import { exorciseHauntedHero } from './moves/exorciseHauntedHero.js';
import { healWounds } from './moves/healWounds.js';
import { endMatchEarly } from './moves/endMatchEarly.js';
import { dodgeCard } from './moves/dodgeCard.js';
import { phaseCard } from './moves/phaseCard.js';
import { useRitualArtifact } from './moves/ritualArtifact.js';
import { operatorSetSupplyCount, operatorMoveToDeckTop, operatorRevealNow, operatorMoveCard, operatorAddEconomy, operatorEscapeVillain, } from './moves/operatorSetters.js';
import { fightMastermind } from './moves/fightMastermind.js';
import { resetTurnEconomy } from './economy/economy.logic.js';
import { runAllInvariantChecks } from './invariants/runAllChecks.js';
import { buildUIState } from './ui/uiState.build.js';
import { filterUIStateForAudience } from './ui/uiState.filter.js';
// why: The registry must be available to Game.setup() for ext_id validation,
// but boardgame.io's setup function signature does not include a registry
// parameter. This module-level holder allows the server to configure the
// registry at startup (via setRegistryForSetup) before any matches are
// created. Tests that bypass registry validation do not set this.
let gameRegistry;
/**
 * Configures the card registry used by Game.setup() for match validation
 * and initial state construction. Must be called by the server at startup
 * before creating any matches.
 *
 * @param registry - The card registry for ext_id existence checks.
 */
export function setRegistryForSetup(registry) {
    gameRegistry = registry;
}
/**
 * Clears the registry previously set by setRegistryForSetup. Test-only —
 * never call in production server code.
 *
 * Without this, a test that calls setRegistryForSetup would leave the
 * registry set for all subsequent tests in the same process, causing
 * test pollution.
 */
export function clearRegistryForSetup() {
    gameRegistry = undefined;
}
// why: No-op registry satisfies the CardRegistryReader interface when the
// real registry has not been configured. Used only in test contexts where
// setup validation is intentionally skipped.
// why: D-10014 — satisfies wider CardRegistryReader for test-context skip
// path. PS-3 widened the interface to `{ listCards, listSets, getSet }`
// so the validator can build per-field qualified-ID sets; the empty
// registry must satisfy the wider shape too.
const EMPTY_REGISTRY = {
    listCards: () => [],
    listSets: () => [],
    getSet: () => undefined,
};
/**
 * Advances the current turn stage to the next stage in the canonical
 * sequence (start -> main -> cleanup -> turn ends).
 *
 * Delegates entirely to advanceTurnStage from turnLoop.ts, which uses
 * getNextTurnStage for ordering — no stage strings are hardcoded here.
 *
 * @param context - boardgame.io move context with G and events.
 */
function advanceStage({ G, ...context }) {
    // why: WP-803 / D-24692 — keep the whole move context rest so the beginMain binding below
    // hands replayOutOfTimeCards exactly what playCard hands applyCardPlay.
    const { ctx, events, random } = context;
    // why: block-all guard (D-24008) — while a KO-a-Hero choice is pending the
    // board is frozen; advanceStage (at any stage) returns with no side effects
    // so the player resolves the Fight effect before any other action. Placed
    // before any G read/write and before the cleanup turn-end check below.
    if (hasPendingKoHeroChoice(G)) {
        return;
    }
    // why: block-all guard (D-24282) — while a Doombot scry-KO choice is pending
    // the board is frozen; advanceStage (at any stage) returns with no side effects
    // so the player picks which revealed card to KO before any other action. Mirrors
    // the D-24008 KO-hero check above (both freeze the cleanup turn-end too).
    if (hasPendingScryKoChoice(G)) {
        return;
    }
    // why: block-all guard (WP-603 / D-24413) — while a Melter Fight KO/keep choice is
    // pending the board is frozen; advanceStage (at any stage) returns with no side
    // effects so the fighting player resolves every revealed deck top before any other
    // action. Mirrors the scry-ko check above (also freezes the cleanup turn-end).
    if (hasPendingMelterKoChoice(G)) {
        return;
    }
    // why: block-all guard (WP-695 / D-24512) — while a Ruthless Dictator scry-3 choice
    // is pending the board is frozen; advanceStage (at any stage) returns with no side
    // effects so the defeating player dispositions every revealed card before any other
    // action. Mirrors the melter check above (also freezes the cleanup turn-end).
    if (hasPendingRuthlessDictatorChoice(G)) {
        return;
    }
    // why: block-all guard (WP-695 / D-24512) — while an Electromagnetic Bubble X-Men
    // pick is pending the board is frozen; advanceStage returns with no side effects so
    // the defeating player picks which X-Men Hero to add before any other action.
    if (hasPendingElectromagneticBubbleChoice(G)) {
        return;
    }
    // why: block-all guard (WP-476 / D-24284) — while a discard-to-limit choice is
    // pending the board is frozen; advanceStage (at any stage) returns with no side
    // effects so the current player picks which cards to discard. A choice parked at
    // the START stage must block the start→main advance until resolved.
    if (hasPendingDiscardChoice(G)) {
        return;
    }
    // why: block-all guard (WP-538 / D-24347) — while a put-cards-on-deck choice is
    // pending the board is frozen; advanceStage returns with no side effects so the
    // current player picks which cards to put on top before any other action.
    if (hasPendingPutCardsOnDeckChoice(G)) {
        return;
    }
    // why: block-all guard (WP-479 / D-24286) — while a reveal-remainder reorder
    // choice is pending the board is frozen; advanceStage returns with no side effects
    // so the current player picks their deck-top order before any other action.
    if (hasPendingReorderChoice(G)) {
        return;
    }
    // why: block-all guard (WP-486 / D-24291) — while a defeat-with-a-Bystander choice
    // is pending the board is frozen; advanceStage returns with no side effects so the
    // current player picks which target to defeat before the stage advances or the
    // cleanup turn-end fires (the pending queue must be empty at turn-end).
    if (hasPendingDefeatChoice(G)) {
        return;
    }
    // why: block-all guard (WP-693 / D-24510) — while a KO-from-discard choice is
    // pending (Loki's Maniacal Tyrant) the board is frozen; advanceStage returns with no
    // side effects so the active player selects which discard cards to KO before the stage
    // advances or the cleanup turn-end fires (the pending queue must be empty at turn-end).
    if (hasPendingKoDiscardChoice(G)) {
        return;
    }
    // why: block-all guard (D-24019) — optional-KO-reward choice pending; the
    // board is frozen until resolved (this also blocks the cleanup turn-end
    // auto-transition below, mirroring the D-24008 KO-hero check above).
    if (hasPendingPlayVillainTopChoice(G))
        return; // why: WP-663 / D-24474 — block-all guard (Shadowed Thoughts play-villain-top choice)
    if (hasPendingOptionalKoReward(G)) {
        return;
    }
    // why: WP-676 / D-24492 — block-all guard: while a Smash discard-for-attack choice is
    // pending the board is frozen (turn-end included), so no other move proceeds until the
    // player discards a hand card or declines. Without this a player could fight/recruit/draw
    // — or end the turn — with a parked Smash choice dangling (freeze / lost choice).
    if (hasPendingSmashDiscard(G)) {
        return;
    }
    // why: WP-700 / D-24519 — block-all guard: while a put-a-hand-card-on-deck-top choice is
    // pending the board is frozen (turn-end included), so no other move proceeds until the
    // player places one hand card on top of their deck. The placement is mandatory (no decline),
    // so leaving it dangling would freeze the game / lose the choice.
    if (hasPendingPutHandOnDeckTop(G)) {
        return;
    }
    // why: WP-702 / D-24521 — block-all guard: while a reveal-top discard-or-keep choice is
    // pending the board is frozen (turn-end included), so no other move proceeds until the player
    // dispositions every revealed deck top. This also freezes each revealed deck top so the
    // snapshot cannot drift before the resolve discards or keeps the exact top card.
    if (hasPendingRevealTopDispose(G)) {
        return;
    }
    // why: WP-753 / D-24580 — block-all guard: while a reveal-three draw / discard / KO
    // assignment is pending the board is frozen (turn-end included) until the player assigns
    // every revealed card. This also freezes the revealed deck top so the snapshot cannot drift.
    if (hasPendingRevealThreeAssign(G)) {
        return;
    }
    // why: WP-681 / D-24498 — block-all guard: while a Do-Over accept/decline choice is
    // pending the board is frozen (turn-end included) until the player accepts or declines.
    if (hasPendingDoOver(G)) {
        return;
    }
    // why: block-all — pendingVictoryPileCardPick must be resolved before any other action (D-24067)
    if (hasPendingVictoryPileCardPick(G)) {
        return;
    }
    // why: block-all — pendingDrawOrEmpowered must be resolved before any other action (D-24069)
    if (hasPendingDrawOrEmpowered(G)) {
        return;
    }
    // why: block-all — pendingCoveringFireChoices must be resolved before any other action (WP-719 / D-24541)
    if (hasPendingCoveringFireChoice(G)) {
        return;
    }
    // why: block-all — pendingMoveVillainChoices must be resolved before any other action (WP-795 / D-24664)
    if (hasPendingMoveVillainChoice(G)) {
        return;
    }
    // why: block-all — pendingSplitFaceChoices must be resolved before any other action (WP-724 / D-24546)
    if (hasPendingSplitFaceChoice(G)) {
        return;
    }
    // why: block-all — pendingCountScaledChoice must be resolved before any other action (WP-675 / D-24490)
    if (hasPendingCountScaledChoice(G)) {
        return;
    }
    // why: block-all — pendingUndercoverChoice must be resolved before any other action (WP-678 / D-24494)
    if (hasPendingUndercoverChoice(G)) {
        return;
    }
    // why: block-all — pendingOptionalPutBottomHQ must be resolved before any other action
    if (hasPendingOptionalPutBottomHQ(G)) {
        return;
    }
    // why: D-24132 — block-all — pendingPutAnyNumberBottomHQ (multi-select HQ→bottom) must be resolved before any other action
    if (hasPendingPutAnyNumberBottomHQ(G)) {
        return;
    }
    // why: block-all — pendingReturnZeroCostDiscard must be resolved before any other action (D-24139)
    if (hasPendingReturnZeroCostDiscard(G)) {
        return;
    }
    // why: block-all — pendingDiscardToPlay must be resolved before any other action (WP-383 / D-24184)
    if (hasPendingDiscardToPlay(G)) {
        return;
    }
    // why: block-all — pendingReturnOnDiscard (optional return-a-discarded-card) must be resolved before any other action (WP-498 / D-24301)
    if (hasPendingReturnOnDiscard(G)) {
        return;
    }
    // why: block-all — pendingGiveHqHeroChoice (interactive give-HQ-Hero pick, Paibok Fight) must be resolved before any other action (WP-532 / D-24343)
    if (hasPendingGiveHqHeroChoice(G)) {
        return;
    }
    // why: block-all — pendingCopyPowersChoice (interactive copy-a-Hero pick, Rogue's Copy Powers) must be resolved before any other action (WP-535 / D-24345)
    if (hasPendingCopyPowersChoice(G)) {
        return;
    }
    // why: WP-684 / D-24501 — block-all: a non-active/multi-seat seat choice freezes turn
    // progress (turn-end included) until EVERY addressed seat has resolved. Only the addressed
    // seats' resolveSeatChoice move proceeds; every action move (this one included) returns.
    if (hasPendingSeatChoice(G)) {
        return;
    }
    // why: D-24515 — the start-of-turn villain reveal is MANDATORY (tabletop Legendary — the
    // active player plays the top of the Villain Deck before doing anything else).
    // Advancing start→main before it would silently skip the reveal, a fairness hole:
    // a player could dodge villains entering the City, Ambushes, Scheme Twists, and
    // Master Strikes simply by advancing past their own reveal. Block the start→main
    // advance until revealVillainCard has set villainRevealedThisTurn (it sets the
    // flag unconditionally once the once-per-turn allowance is spent, even on an
    // exhausted-deck no-op). The autoplay bot (autoplay.mjs) and the sim AI always
    // reveal first — reveal outscores advance 400:10 in ai.competent — so this never
    // no-ops their advance; ai.legalMoves mirrors this guard (advanceStage is not a
    // legal start-stage move until the reveal is spent) so the bot never faults on it.
    if (G.currentStage === 'start' && !G.villainRevealedThisTurn) {
        return;
    }
    // why: turn cannot end while a player-choice reveal is pending; at cleanup,
    // advanceTurnStage would otherwise call events.endTurn() and bypass the
    // endTurn-move guard (D-22002). The KO turn-end block is already covered by
    // the block-all guard above (D-24008 — queue-non-empty blocks turn-end).
    if (G.currentStage === 'cleanup' && G.pendingHeroChoice !== undefined) {
        return;
    }
    // why: WP-696 / D-24513 — pass ctx.currentPlayer and FORWARD the optional
    // `{ next }` arg so advanceTurnStage can honor a queued extra turn on this
    // KO-driven auto-end path exactly as it does on the normal cleanup auto-end
    // (a bare `() => events.endTurn()` here would silently drop the `{ next }`).
    advanceTurnStage(G, {
        currentPlayer: ctx.currentPlayer,
        events: { endTurn: (opts) => events.endTurn(opts) },
        // why: WP-701 / D-24520 — bind the end-of-turn cleanup (discard + draw the new hand)
        // for the ending seat so the cleanup-stage turn-end path (D-22002) draws too. The
        // closure carries G + the ShuffleProvider (ctx.random) that turnLoop.ts's narrow
        // state interface deliberately does not.
        cleanup: (endingPlayerID) => applyEndOfTurnCleanup(G, endingPlayerID, { random }),
        // why: WP-803 / D-24692 — rules v23 L1678: returning Out of Time cards are played after the
        // villain card and before the hand. advanceTurnStage calls this only on start → main, which
        // the reveal gate and the block-all guards above hold back until the villain card is
        // revealed and every reveal choice is resolved.
        beginMain: (startingPlayerID) => replayOutOfTimeCards(G, context, startingPlayerID),
    });
}
/**
 * Reshapes the client-visible state from LegendaryGameState to
 * audience-filtered UIState. Registered on LegendaryGame.playerView so
 * every state frame boardgame.io pushes to a connected client is already
 * audience-filtered — clients never observe raw G.
 *
 * Pure function: no I/O, no RNG, no mutation of G or ctx, no entries
 * appended to G.messages, never throws. Given identical (G, ctx, playerID),
 * the output is byte-identical.
 *
 * Runs on every state push — keep cheap. Delegates to WP-028 / WP-029
 * helpers which already carry the copy discipline that prevents aliasing
 * G into the projection.
 *
 * // why: parameter shape is the single context object { G, ctx, playerID }
 * because boardgame.io 0.50.2's Game<G>['playerView'] is declared as
 * `(context: { G, ctx, playerID }) => any`. The engine call-site is the
 * boardgame.io runtime itself — it passes a single object. A three-arg
 * positional signature would be type-compatible only via a double-cast and
 * would still be wrong at runtime. WP-089 / EC-089 / RS-3 locked a
 * three-arg signature that reflected the WP's design intent but conflicted
 * with the library's actual runtime shape under TS `exactOptionalPropertyTypes`;
 * resolved during execution per user-authorized RS-3 refinement (see
 * 01.6 post-mortem — "RS-3 cast refinement").
 *
 * @param context - boardgame.io single-argument context: { G, ctx, playerID }.
 * @returns Audience-filtered UIState for the viewing client.
 */
function buildPlayerView({ G, ctx, playerID, }) {
    const uiBuildContext = {
        phase: ctx.phase,
        turn: ctx.turn,
        currentPlayer: ctx.currentPlayer,
    };
    const fullUIState = buildUIState(G, uiBuildContext);
    // why: null and any non-string playerID map to spectator because
    // boardgame.io represents unauthenticated / unseated clients as null
    // on the WebSocket transport; runtime paths that accidentally pass
    // undefined are defended by the same typeof check. Empty string '' is
    // NOT mapped to spectator: it is a valid seat ID in the 0.50.x
    // "0" | "1" | ... convention and routes to { kind: 'player', playerId: '' }.
    // EC-089 §Locked Values line 25 locks the typeof check verbatim.
    const audience = typeof playerID === 'string'
        ? { kind: 'player', playerId: playerID }
        : { kind: 'spectator' };
    return filterUIStateForAudience(fullUIState, audience);
}
/**
 * The authoritative boardgame.io Game object for Legendary Arena.
 *
 * All phases, moves, hooks, and endgame logic are registered through this
 * single Game instance. No parallel or experimental Game objects may exist.
 *
 * @see docs/ai/ARCHITECTURE.md -- "The LegendaryGame Object"
 */
export const LegendaryGame = {
    name: 'legendary-arena',
    // why: Legendary supports 1-5 players. Solo play (1) is a core mode in the
    // physical game; 5 is the maximum supported by the base set rules.
    minPlayers: 1,
    maxPlayers: 5,
    // why: D-24702 — boardgame.io 0.50 enables undo by default, and its reducer then
    // keeps `_undo` / `_redo` arrays holding a full { G, ctx, plugins } snapshot for
    // every move of the current turn, persisted in every bgio match record. Nothing
    // uses undo: the game socket accepts only MAKE_MOVE from clients (D-24701), and
    // the faithful replay re-dispatches only MAKE_MOVE log entries (D-24124). The
    // stacks are pure storage cost. G is unaffected, so no hash or replay changes.
    disableUndo: true,
    /**
     * Validates setupData before setup() is called. boardgame.io calls this
     * from the lobby create endpoint -- returning a string triggers a 400
     * response with the string as the error message.
     *
     * why: this hook is boardgame.io's ONLY create-time path to a clean,
     * client-visible rejection. A string return becomes `ctx.throw(400, msg)`
     * in boardgame.io's CreateMatch handler (dist/cjs/server.js), and Koa
     * exposes 4xx bodies — so the caller sees the actual reason. If a bad
     * config is instead left to fail inside setup() (which throws), Koa
     * swallows the throw into a generic HTTP 500 "Internal Server Error" and
     * the real reason (e.g. "bystandersCount must be at least 30 …") never
     * reaches the player. So the full match-setup validation runs HERE, at the
     * lobby boundary, and the setup() throw below remains only as a
     * belt-and-suspenders net for direct/internal callers (tests, rematch).
     *
     * The registry is the module-level `gameRegistry` (set by the server via
     * setRegistryForSetup at startup). When no registry is configured — unit
     * tests that intentionally skip validation — this hook skips the
     * existence/floor checks exactly like setup() does, returning only the
     * missing-setupData error.
     *
     * @param setupData - the setupData from the create request body
     * @param numPlayers - player count; drives the WP-370 composition gate
     * @returns an error message string if invalid, undefined if valid
     */
    validateSetupData: (setupData, numPlayers) => {
        if (!setupData) {
            return 'Missing setupData. The create request must include a setupData ' +
                'object with a valid MatchConfiguration (schemeId, mastermindId, etc.).';
        }
        // why: run the authoritative match-setup validation here so an invalid
        // config (unknown ext_id, below-floor supply count, malformed field,
        // or — WP-370 — a composition that does not match the player count)
        // returns a message-bearing HTTP 400 from the lobby create endpoint
        // instead of an opaque HTTP 500 from a setup() throw. Guarded on
        // gameRegistry for the no-registry test path (mirrors setup()).
        if (gameRegistry) {
            const result = validateMatchSetup(setupData, gameRegistry, numPlayers);
            if (!result.ok) {
                const firstError = result.errors[0];
                return firstError
                    ? firstError.message
                    : 'Match setup validation failed with an unknown error.';
            }
        }
        return undefined;
    },
    /**
     * Initializes the game state from a MatchConfiguration payload.
     *
     * setup() is the single external-input boundary for the engine. After this
     * function returns, the engine operates solely on G and ctx -- no further
     * external configuration is accepted.
     *
     * @param context - boardgame.io setup context (ctx, events, random, log)
     * @param matchConfiguration - the match setup payload with card ext_ids and counts
     * @returns the initial LegendaryGameState
     * @throws {Error} if matchConfiguration is not provided or validation fails
     */
    // why: setup() accepts MatchConfiguration so the server can pass in the
    // match parameters chosen during lobby/matchmaking. This is the only point
    // where external configuration enters the engine.
    setup: (context, matchConfiguration) => {
        // why: validateSetupData guards this at the lobby API layer, but setup()
        // can also be called directly in tests or by boardgame.io internals
        // (e.g. rematch). This check is a belt-and-suspenders safety net.
        if (!matchConfiguration) {
            throw new Error('LegendaryGame.setup() requires a MatchConfiguration argument. ' +
                'The server must pass the match setup payload when creating a new game.');
        }
        // why: When a registry is available (set by the server via
        // setRegistryForSetup), validate the config against the registry before
        // building state. This catches invalid ext_ids at match creation time.
        // Game.setup() is the ONLY place in the engine where throwing is correct
        // — an invalid config must abort match creation immediately.
        if (gameRegistry) {
            // why: WP-370 — pass ctx.numPlayers so the composition gate can reject a
            // loadout whose villain-group / henchman / hero counts do not match the
            // player count (belt-and-suspenders behind validateSetupData).
            const result = validateMatchSetup(matchConfiguration, gameRegistry, context.ctx.numPlayers);
            if (!result.ok) {
                const firstError = result.errors[0];
                const errorMessage = firstError
                    ? firstError.message
                    : 'Match setup validation failed with an unknown error.';
                throw new Error(errorMessage);
            }
        }
        // why: The registry parameter allows buildInitialGameState to resolve
        // card data in future Work Packets (hero deck, villain deck construction).
        // For WP-005B, starting decks and piles use well-known ext_ids that do
        // not require registry lookup. EMPTY_REGISTRY is used when the server has
        // not configured a registry (e.g., in unit tests).
        const registryForSetup = gameRegistry ?? EMPTY_REGISTRY;
        // why: runAllInvariantChecks enforces structural, gameRules,
        // determinism, and lifecycle invariants after buildInitialGameState
        // constructs G. Setup is the one engine-wide call site permitted to
        // throw per .claude/skills/legendary-game-engine/SKILL.md §Throwing Convention row 1,
        // so assertInvariant's throw is safe here. Per D-3102, per-move
        // wiring is deferred to a follow-up WP.
        const initialState = buildInitialGameState(matchConfiguration, registryForSetup, context);
        runAllInvariantChecks(initialState, {
            phase: context.ctx.phase,
            turn: context.ctx.turn,
        });
        return initialState;
    },
    // why: WP-411 / D-24223 — the end-of-game condition is a TOP-LEVEL Game
    // `endIf`, not a phase `endIf`. In boardgame.io a phase `endIf` only ends the
    // PHASE; only a top-level Game `endIf` sets `ctx.gameover` (to the truthy
    // return) and ends the game, which is what boardgame.io's store then persists
    // into the match `metadata.gameover`. The prior implementation placed the
    // endgame check as a `play`-phase `endIf`, which ended the phase (→ the empty
    // `end` phase) but NEVER set `ctx.gameover` — so no match ever registered
    // gameover and every completed-match consumer (competitive submission WP-338,
    // result-LAGN WP-406, Hall of Legends WP-407/408, match summaries D-24169) was
    // unreachable in production. This is a pure read of `G.counters` via
    // `evaluateEndgame` (the sole endgame authority; no inline counter logic, no
    // I/O, no events, consumes no randomness) — it returns the EndgameResult
    // ({ outcome, reason }) for a terminal state so the framework sets
    // `ctx.gameover` to it, or `undefined` to let the game continue. The
    // top-level `Game['endIf']` return type is `any`, so — unlike the removed
    // phase-level check whose narrower `boolean | void` type forced a cast — no
    // cast is needed. Determinism: this mutates no `G`, so an identical `G` hashes
    // identically; the only behavioral change is that a game now TERMINATES at the
    // win/loss point instead of accepting further moves.
    endIf: ({ G }) => evaluateEndgame(G) ?? undefined,
    // why: playerView is the sole engine→client projection boundary.
    // Clients never observe raw LegendaryGameState — this hook reshapes
    // every state frame into audience-filtered UIState via buildUIState
    // (WP-028) + filterUIStateForAudience (WP-029). filterUIStateForAudience
    // is the project's audience authority per D-0302 / D-8901; boardgame.io's
    // built-in secret-stripping helper is intentionally not used (see D-8901).
    //
    // why: cast anchor is NonNullable<Game<LegendaryGameState>['playerView']>
    // (narrowest refinement of RS-3's locked `Game<...>['playerView']` anchor).
    // The refinement strips the `| undefined` half of the indexed-access type
    // that exactOptionalPropertyTypes: true otherwise carries onto the object
    // literal field. It preserves RS-3's intent (anchor to boardgame.io's
    // Game<...>.playerView property type; do NOT modify the Game<...> generic)
    // while satisfying the compiler. Documented as an RS-3 cast refinement in
    // the 01.6 post-mortem; no DECISIONS entry needed — tooling variance, not
    // architecture.
    playerView: buildPlayerView,
    // why: every state-mutating move is registered in long-form with
    // `client: false` per D-10008. Background: `playerView` (above) reshapes
    // the engine's authoritative G (LegendaryGameState) into UIState — a
    // completely different shape — for delivery to clients. Without
    // `client: false`, boardgame.io's default behavior runs each move
    // optimistically on the client's local G, which is actually UIState
    // (no `playerZones`, no `lobby`, no `villainDeck`, etc.). Every
    // state-mutating move would crash on the client with "Cannot read
    // properties of undefined" the first time it tries to mutate a G field
    // that doesn't exist on UIState. Setting `client: false` makes each
    // move server-only — the client dispatches via the multiplayer
    // transport but never tries to run the move locally. Server-side
    // execution against real G works; the next server frame broadcasts
    // the new state via playerView. This pattern applies uniformly to
    // ALL state-mutating moves in the engine. The 01.5 triggers remain
    // absent: no new LegendaryGameState field, no buildInitialGameState
    // shape change, no new LegendaryGame.moves entry (the move *names* and
    // their function bodies are unchanged — only the registration form
    // changes from short-form `move` to long-form `{ move, client: false }`),
    // and no new phase hook. Logged as D-10008.
    moves: {
        drawCards: { move: drawCards, client: false },
        playCard: { move: playCard, client: false },
        endTurn: { move: endTurn, client: false },
        advanceStage: { move: advanceStage, client: false },
        revealVillainCard: { move: revealVillainCard, client: false },
        fightVillain: { move: fightVillain, client: false },
        recruitHero: { move: recruitHero, client: false },
        // why: WP-648 / D-24460 — recruitOfficer buys the top S.H.I.E.L.D. Officer
        // supply token for 3 recruit into the current player's discard. Non-core,
        // internally stage-gated (the recruitHero pattern). Server-only (client:
        // false) per D-10008 — it mutates real G (G.piles.officers / playerZones /
        // turnEconomy), absent on UIState.
        recruitOfficer: { move: recruitOfficer, client: false },
        // why: WP-842 / D-24776 — recruitSidekick buys the top Sidekick Stack token for 2
        // recruit into the current player's discard, once per turn. Non-core, internally
        // stage-gated (the recruitOfficer pattern). Server-only (client: false) per D-10008 —
        // it mutates real G (G.piles.sidekicks / playerZones / turnEconomy).
        recruitSidekick: { move: recruitSidekick, client: false },
        // why: WP-757 / D-24587 — exorciseHauntedHero pays a Haunted Hero's cost, KOs or
        // gives that Hero, and releases its haunter (a Villain enters the City ignoring
        // Ambush). Non-core, internally stage-gated (the recruitHero pattern). Server-only
        // (client: false) per D-10008 — it mutates real G, absent on UIState.
        exorciseHauntedHero: { move: exorciseHauntedHero, client: false },
        // why: D-24051 — dodgeCard is a non-core, internally-stage-gated move (the
        // recruitHero pattern), NOT a core move; registered here so the player can
        // discard a Dodge card from hand to draw a replacement. Server-only
        // (client: false) per D-10008 — it mutates real G (playerZones), absent on UIState.
        dodgeCard: { move: dodgeCard, client: false },
        fightMastermind: { move: fightMastermind, client: false },
        // why: WP-379 / D-24179 — healWounds uses the printed Wound "Healing" ability,
        // KO'ing all Wounds from the current player's hand to G.ko. Server-only
        // (client: false) per D-10008 — it mutates real G (playerZones.hand / G.ko),
        // absent on UIState.
        healWounds: { move: healWounds, client: false },
        // why: WP-783 / D-24629 — phaseCard is a non-core, internally-stage-gated move (the
        // dodgeCard pattern), NOT a core move; registered here so the player can swap a
        // Phasing card in hand with the top card of their deck. Server-only (client: false)
        // per D-10008 — it mutates real G (playerZones), absent on UIState. Not emitted by
        // getLegalMoves, so bots never phase.
        phaseCard: { move: phaseCard, client: false },
        // why: WP-858 / D-24794 — useRitualArtifact is a non-core, internally-stage-gated move (the
        // phaseCard pattern): the player discards a Ritual Artifact they played this turn or control
        // for its reward once its condition held. Server-only (client: false) per D-10008 — it mutates
        // real G (playerZones, controlledArtifacts, turnEconomy). Not emitted by getLegalMoves, so
        // bots never use a Ritual.
        useRitualArtifact: { move: useRitualArtifact, client: false },
        // why: WP-502 / D-24306 — endMatchEarly is the player-initiated "End Game"
        // control: it latches the MATCH_ENDED_EARLY endgame counter so the match ends
        // (endedEarly tie) for every seat via the top-level endIf. Server-only
        // (client: false) per D-10008 — it mutates real G (G.counters), absent on
        // UIState. NOT in CORE_MOVE_NAMES (mirrors the resolve* / healWounds
        // non-core moves).
        endMatchEarly: { move: endMatchEarly, client: false },
        resolveHeroChoice: { move: resolveHeroChoice, client: false },
        resolveKoHeroChoice: { move: resolveKoHeroChoice, client: false },
        // why: WP-470 / D-24282 — resolveScryKoChoice resolves the interactive Doombot
        // scry-KO choice (pick which of the top two deck cards to KO). Server-only
        // (client: false) per D-10008 — it mutates real G (playerZones.deck / G.ko),
        // absent on UIState. NOT in CORE_MOVE_NAMES (mirrors resolveKoHeroChoice).
        resolveScryKoChoice: { move: resolveScryKoChoice, client: false },
        // why: WP-603 / D-24413 — resolveMelterKoChoice resolves the interactive Melter
        // Fight KO/keep choice (the fighting player KOs or keeps each player's revealed
        // deck top). Server-only (client: false) per D-10008 — it mutates real G
        // (playerZones.deck / G.ko), absent on UIState. NOT in CORE_MOVE_NAMES (mirrors
        // resolveScryKoChoice).
        resolveMelterKoChoice: { move: resolveMelterKoChoice, client: false },
        // why: WP-695 / D-24512 — resolveRuthlessDictatorChoice resolves the interactive
        // Red Skull Ruthless Dictator scry-3 disposition (KO one / discard one / top one,
        // one revealed card per call). Server-only (client: false) per D-10008 — it mutates
        // real G (playerZones.deck / .discard / G.ko), absent on UIState. NOT in
        // CORE_MOVE_NAMES (mirrors resolveMelterKoChoice).
        resolveRuthlessDictatorChoice: { move: resolveRuthlessDictatorChoice, client: false },
        // why: WP-695 / D-24512 — resolveElectromagneticBubbleChoice resolves the interactive
        // Magneto Electromagnetic Bubble X-Men Hero pick (recorded as a deferred hand
        // injection consumed at the player's next onBegin fill). Server-only (client: false)
        // per D-10008 — it mutates real G (G.deferredHandInjections), absent on UIState. NOT
        // in CORE_MOVE_NAMES (mirrors resolveRuthlessDictatorChoice).
        resolveElectromagneticBubbleChoice: { move: resolveElectromagneticBubbleChoice, client: false },
        // why: WP-476 / D-24284 — resolveDiscardChoice resolves the interactive
        // Magneto discard-to-limit choice (the current player picks which cards to
        // discard down to four). Server-only (client: false) per D-10008 — it mutates
        // real G (playerZones.hand / .discard), absent on UIState. NOT in
        // CORE_MOVE_NAMES (mirrors resolveScryKoChoice / resolveKoHeroChoice).
        resolveDiscardChoice: { move: resolveDiscardChoice, client: false },
        // why: WP-538 / D-24347 — resolvePutCardsOnDeckChoice resolves the interactive
        // core Dr. Doom put-2-on-top choice (the current player picks which cards to put
        // on top of their deck, in top order). Server-only (client: false) per D-10008 —
        // it mutates real G (playerZones.hand / .deck), absent on UIState. NOT in
        // CORE_MOVE_NAMES (mirrors resolveDiscardChoice / resolveReorderChoice).
        resolvePutCardsOnDeckChoice: { move: resolvePutCardsOnDeckChoice, client: false },
        // why: WP-479 / D-24286 — resolveReorderChoice resolves the interactive
        // reveal-remainder reorder ("put the rest back in any order"): the current player
        // picks the top-of-deck order for the revealed-but-not-drawn cards. Server-only
        // (client: false) per D-10008 — it mutates real G (playerZones.deck), absent on
        // UIState. NOT in CORE_MOVE_NAMES (mirrors resolveScryKoChoice / resolveDiscardChoice).
        resolveReorderChoice: { move: resolveReorderChoice, client: false },
        // why: WP-486 / D-24291 — resolveDefeatChoice resolves the interactive Silent
        // Sniper defeat-with-a-Bystander choice: the current player picks which Villain
        // (by City space) or the Mastermind to defeat for free. Server-only (client:
        // false) per D-10008 — it mutates real G (city / victory / mastermind), absent on
        // UIState. NOT in CORE_MOVE_NAMES (mirrors resolveReorderChoice).
        resolveDefeatChoice: { move: resolveDefeatChoice, client: false },
        // why: WP-693 / D-24510 — resolveKoDiscardChoice resolves the interactive Loki
        // Maniacal Tyrant KO-from-discard choice (the active player selects 0..4 of their
        // own discard cards to KO into G.ko). Server-only (client: false) per D-10008 — it
        // mutates real G (playerZones.discard / G.ko), absent on UIState. NOT in
        // CORE_MOVE_NAMES (mirrors resolveDefeatChoice / resolvePutCardsOnDeckChoice).
        resolveKoDiscardChoice: { move: resolveKoDiscardChoice, client: false },
        resolveOptionalKoReward: { move: resolveOptionalKoReward, client: false },
        // why: WP-676 / D-24492 — resolves a Smash "discard another card from your hand for +N
        // Attack?" choice (discard grants +N to G.turnEconomy.attack; decline does nothing).
        // client:false — the engine computes the Attack grant; dispatched by SmashDiscardPrompt.
        resolveSmashDiscard: { move: resolveSmashDiscard, client: false },
        // why: WP-700 / D-24519 — server-only: the client submits the { cardId } intent; the engine
        // moves the chosen hand card to the deck top. Mandatory placement (no decline arm).
        resolvePutHandOnDeckTop: { move: resolvePutHandOnDeckTop, client: false },
        // why: WP-702 / D-24521 — server-only: the client submits the { ownerPlayerID, cardId,
        // disposition } intent; the engine discards or keeps that exact revealed deck-top card.
        resolveRevealTopDispose: { move: resolveRevealTopDispose, client: false },
        // why: WP-753 / D-24580 — server-only: the client submits the { cardId, disposition }
        // intent; the engine draws / discards / KOs that revealed card (and re-reveals on a repeat).
        resolveRevealThreeAssign: { move: resolveRevealThreeAssign, client: false },
        // why: WP-681 / D-24498 — Do-Over accept/decline resolver; server-only (client submits
        // intent { accept: true } / { decline: true }, the engine performs the discard + draw).
        resolveDoOver: { move: resolveDoOver, client: false },
        // why: WP-663 / D-24474 — resolves Shadowed Thoughts' "play the top Villain-Deck card
        // for +2 Attack?" choice (accept plays the top card via the shared reveal cascade + grants
        // +Attack; decline does nothing). client:false — dispatched by the PlayVillainTopPrompt.
        resolvePlayVillainTopChoice: { move: resolvePlayVillainTopChoice, client: false },
        resolveOptionalPutBottomHQ: { move: resolveOptionalPutBottomHQ, client: false },
        // why: D-24132 — resolvePutAnyNumberBottomHQ resolves the multi-select HQ→bottom choice
        // (Wonder Man's 8th Wonder of the World et al.). Server-only (client: false) per D-10008 —
        // it mutates real G (G.hq / G.heroDeck / G.turnEconomy), absent on UIState.
        resolvePutAnyNumberBottomHQ: { move: resolvePutAnyNumberBottomHQ, client: false },
        resolveVictoryPileCardPick: { move: resolveVictoryPileCardPick, client: false },
        resolveDrawOrEmpowered: { move: resolveDrawOrEmpowered, client: false },
        // why: WP-719 / D-24541 — resolveCoveringFireChoice resolves Hawkeye's Covering Fire
        // choose-one (each other player draws or discards a card). Server-only (client: false) —
        // it mutates real G (each other seat's hand/deck/discard), absent on UIState.
        resolveCoveringFireChoice: { move: resolveCoveringFireChoice, client: false },
        // why: WP-795 / D-24664 — resolveMoveVillainChoice resolves core Storm's Spinning Cyclone
        // (move a Villain to a new City space, swap if occupied, rescue only its Bystanders, or
        // decline). Server-only (client: false) — it mutates real G (G.city, attachedBystanders, the
        // chooser's Victory Pile) and validates the answer against the live City.
        resolveMoveVillainChoice: { move: resolveMoveVillainChoice, client: false },
        // why: WP-724 / D-24546 — resolveSplitFaceChoice binds the chosen side of a split /
        // dual-faced hero card (grants that face's economy + fires its ability). Server-only
        // (client: false) — it mutates real G (inPlay relabel, turnEconomy, effects), absent on UIState.
        resolveSplitFaceChoice: { move: resolveSplitFaceChoice, client: false },
        resolveCountScaledChoice: { move: resolveCountScaledChoice, client: false },
        // why: WP-678 / D-24494 — resolveUndercoverChoice resolves the pending Undercover
        // target pick (send a chosen [team:shield] Hero from hand to the Victory Pile).
        // Server-only (client: false) — it mutates real G (victory + undercover tracker).
        resolveUndercoverChoice: { move: resolveUndercoverChoice, client: false },
        // why: D-24139 — resolveReturnZeroCostDiscard resolves the mandatory 0-cost
        // discard-to-hand return (Black Knight's Defend the Weak). Server-only
        // (client: false) per D-10008 — it mutates playerZones fields.
        resolveReturnZeroCostDiscard: { move: resolveReturnZeroCostDiscard, client: false },
        resolveDiscardToPlay: { move: resolveDiscardToPlay, client: false },
        // why: WP-498 / D-24301 — resolveReturnOnDiscard resolves the OPTIONAL
        // return-a-discarded-card choice (Cyclops Unending Energy). Server-only
        // (client: false) per D-10008 — it mutates playerZones fields.
        resolveReturnOnDiscard: { move: resolveReturnOnDiscard, client: false },
        // why: WP-532 / D-24343 — resolveGiveHqHeroChoice resolves the interactive
        // give-HQ-Hero choice (Paibok the Power Skrull Fight). Server-only
        // (client: false) per D-10008 — it mutates G.hq / G.heroDeck / playerZones.
        // NOT in CORE_MOVE_NAMES (mirrors resolveKoHeroChoice / resolveReturnOnDiscard).
        resolveGiveHqHeroChoice: { move: resolveGiveHqHeroChoice, client: false },
        // why: WP-535 / D-24345 — resolveCopyPowersChoice resolves the interactive
        // copy-a-Hero choice (Rogue's Copy Powers). Server-only (client: false) per
        // D-10008 — it re-fires the copied Hero's ability (mutating G) and grants the
        // copied class. NOT in CORE_MOVE_NAMES (mirrors resolveGiveHqHeroChoice).
        resolveCopyPowersChoice: { move: resolveCopyPowersChoice, client: false },
        // why: WP-684 / D-24501 — resolveSeatChoice resolves one addressed seat's selection for
        // a non-active/multi-seat pending choice (foundational; no card wired yet). Server-only
        // (client: false) per D-10008 — it mutates real G (G.pendingSeatChoice / G.messages),
        // absent on UIState. NOT in CORE_MOVE_NAMES (mirrors the other resolve* moves). A
        // non-active seat is admitted to submit it via the resolvingSeatChoice stage ride.
        resolveSeatChoice: { move: resolveSeatChoice, client: false },
        // why: WP-806 / D-24713 §7 — the six operator setters run ONLY in an operator match
        // (each gates on G.isDebugMatch, the operator's own turn, the main stage and no open
        // choice, and returns silently otherwise). They are deliberately absent from
        // getLegalMoves, SIMULATION_MOVE_NAMES, both MOVE_MAPs, the determinism-only replay
        // and the fixture runner, so no bot, simulation, PAR run or fixture ever makes one
        // (the phaseCard / endMatchEarly precedent). Server-only (client: false) per D-10008.
        operatorSetSupplyCount: { move: operatorSetSupplyCount, client: false },
        operatorMoveToDeckTop: { move: operatorMoveToDeckTop, client: false },
        operatorRevealNow: { move: operatorRevealNow, client: false },
        operatorMoveCard: { move: operatorMoveCard, client: false },
        operatorAddEconomy: { move: operatorAddEconomy, client: false },
        operatorEscapeVillain: { move: operatorEscapeVillain, client: false },
    },
    // why: phase `next` fields declare the intended linear progression
    // (lobby -> setup -> play -> end) but do not cause automatic transitions.
    // Actual transitions require explicit ctx.events.setPhase() calls, which
    // will be added by subsequent Work Packets.
    phases: {
        lobby: {
            start: true,
            next: 'setup',
            // why: `activePlayers: { all: 'lobbyReady' }` plus the matching
            // empty `stages.lobbyReady: {}` block tells boardgame.io: every
            // seated player (not just ctx.currentPlayer) is in the
            // 'lobbyReady' stage and may submit phase-level moves. Equivalent
            // at runtime to boardgame.io's ActivePlayers.ALL constant (which
            // expands to `{ all: Stage.NULL }` where Stage.NULL: null) — but
            // type-clean because `StageArg = StageName | object` rejects the
            // bare `null` literal even though the runtime accepts it. The
            // empty `stages` block adds no new behavior; the lobby phase's
            // top-level `moves: { setPlayerReady, startMatchIfReady }` are
            // the only callable moves regardless of stage. Without this
            // activePlayers config, boardgame.io's server-side dispatch
            // rejects setPlayerReady from any player other than the
            // turn-holder with "player not active - playerID=[N] -
            // action[setPlayerReady]" — making multi-player lobby ready-up
            // impossible. Logged as D-10007. Per the four 01.5 triggers,
            // this is a phase-configuration property (not a phase hook), no
            // new LegendaryGameState field, no buildInitialGameState shape
            // change, no new LegendaryGame.moves entry — 01.5 NOT INVOKED.
            // The 'lobbyReady' stage name is local to this phase and has no
            // semantic meaning beyond "anonymous stage that authorizes all
            // players to submit phase moves".
            turn: {
                activePlayers: { all: 'lobbyReady' },
                stages: {
                    lobbyReady: {},
                },
            },
            // why: lobby-phase moves are also long-form with `client: false`
            // per D-10008 — same playerView-vs-UIState shape mismatch as the
            // top-level moves. setPlayerReady mutates G.lobby.ready and
            // startMatchIfReady mutates G.lobby.started + calls
            // events.setPhase('play'); both crash on the client because
            // UIState has no `lobby` field. Server-only dispatch is the fix.
            moves: {
                setPlayerReady: { move: setPlayerReady, client: false },
                startMatchIfReady: { move: startMatchIfReady, client: false },
            },
        },
        setup: {
            next: 'play',
        },
        play: {
            next: 'end',
            // why: WP-411 / D-24223 — the endgame check that used to live here as a
            // `play`-phase `endIf` was REMOVED. A phase `endIf` only ends the phase
            // (→ the empty `end` phase); it never set `ctx.gameover`, which was the
            // bug. Termination now lives on the TOP-LEVEL `LegendaryGame.endIf`
            // (above), the only `endIf` that sets `ctx.gameover`. `play.next: 'end'`
            // and the `end` phase are retained (the four-phase structure is pinned by
            // game.test.ts) even though the game now ends via `gameover` before the
            // `end` phase is reached.
            turn: {
                // why: explicit `activePlayers: { currentPlayer: 'playTurn' }` plus
                // empty `stages.playTurn: {}` per D-10009. Without it,
                // boardgame.io's InitTurnOrderState falls back to
                // `SetActivePlayers(ctx, turn.activePlayers || {})` — passing the
                // empty object literal. SetActivePlayers with `{}` returns
                // `ctx.activePlayers = {}` (truthy empty object, NOT null). Then
                // IsPlayerActive evaluates `if (ctx.activePlayers)` → truthy
                // branch → `playerID in {}` → false for ALL players, blocking
                // every move including drawCards from the seated current player.
                // The explicit `{ currentPlayer: 'playTurn' }` config writes
                // `ctx.activePlayers = { '<currentPlayer>': 'playTurn' }`, so
                // IsPlayerActive correctly returns true only for ctx.currentPlayer
                // — restoring turn-based "only current player can move" semantics.
                // The empty `stages.playTurn: {}` block adds no behavior; the
                // top-level LegendaryGame.moves bag remains the active move
                // vocabulary (drawCards, playCard, fightVillain, etc.) per the
                // getMove precedence chain (stage.moves → phase.moves → global
                // moves). The 01.5 triggers remain absent: this is a
                // phase-configuration property, not a phase hook.
                activePlayers: { currentPlayer: 'playTurn' },
                // why: WP-805 / D-24700 — boardgame.io calls order.first once per match, when
                // the play phase begins (startMatchIfReady's setPhase), with the match's
                // seeded random in its context; order.next runs at every endTurn.
                order: {
                    first: ({ G, ctx, random }) => {
                        if (G.isFirstPlayerRandom !== true) {
                            // why: a flagless G (created before WP-805, or solo) keeps
                            // boardgame.io's former TurnOrder.DEFAULT opener for a phase that
                            // starts after turn 0, verbatim and with no random draw, so a stored
                            // log replays unchanged through the faithful replay (D-24119).
                            return (ctx.playOrderPos + 1) % ctx.playOrder.length;
                        }
                        // why: rules v23 L524 ("Choose a random player to go first"), D-24700.
                        // One seeded draw per match, made when play begins, so it comes after
                        // every setup shuffle and leaves the decks untouched. Die(n) returns
                        // 1..n; minus one is a playOrder index.
                        return random.Die(ctx.playOrder.length) - 1;
                    },
                    // why: clockwise rotation (ascending seat order, wrapping), unchanged:
                    // boardgame.io's TurnOrder.DEFAULT next, verbatim.
                    next: ({ ctx }) => (ctx.playOrderPos + 1) % ctx.playOrder.length,
                },
                stages: {
                    playTurn: {},
                    // why: WP-684 / D-24501 — an empty stage a NON-ACTIVE addressed seat is placed
                    // into (via events.setActivePlayers, revert:true, moveLimit:1) while a
                    // non-active/multi-seat pending choice is open, so boardgame.io accepts that seat's
                    // resolveSeatChoice move without forking the { currentPlayer: 'playTurn' } turn
                    // model. Empty: the seat resolves via the global moves bag (stage → phase → global
                    // precedence). Never entered when no seat choice is open, so existing games are
                    // byte-identical (an unused stage adds no runtime behavior and is not in G).
                    [SEAT_CHOICE_STAGE]: {},
                },
                // why: WP-367 / D-24159 — deck-exhaustion "final turn" latch. Fires after
                // EVERY successful play-phase move, so it observes the deck state that the
                // move (including any card effects it ran) leaves behind. The helper
                // latches the final turn the first time either shared deck reaches zero
                // and is sticky — a later card effect that refills the deck does NOT
                // cancel the final turn (the exact rulebook edge Jeff reported). See
                // finalTurn.logic.ts.
                onMove: ({ G, ctx, random, events }) => {
                    latchFinalTurnIfDeckExhausted(G);
                    // why: same cadence as the final-turn latch above — a pile-depletion
                    // scheme (Super Hero Civil War: hero deck) can empty on any move, incl.
                    // twist-driven HQ refills, so the check runs per-move here rather than at
                    // recruitHero. Setting SCHEME_LOSS here pre-empts the deck-exhaustion tie
                    // via evaluateEndgame precedence (SCHEME_LOSS before FINAL_TURN_TIE) and
                    // the tie-resolution guard — no finalTurn.logic change needed (D-24319).
                    applyPileDepletionResourceLoss(G);
                    // why: WP-670 / D-24484 — same per-move cadence: a transforming scheme (Chthon)
                    // flips to its Great Old One face when 5 Bystanders accumulate in the KO pile,
                    // which can happen on any move. Early no-op for every non-transform scheme, so a
                    // normal game does no work here and stays byte-identical.
                    checkAndTransformScheme(G);
                    // why: WP-568 / D-24377 — same per-move cadence as the two checks above,
                    // and for the same reason: it observes the state the move leaves behind,
                    // which is exactly when a turn's recruit total can cross a numeric
                    // threshold a hero ability is waiting on. `{ random }` is passed because a
                    // deferred effect may DRAW, and the bare ctx carries no random (D-24051).
                    resolveDeferredHeroGrants(G, { G, ctx, random });
                    // why: WP-793 / D-24656 — a NON-ACTIVE seat's front return-on-discard entry (a
                    // discard forced on another player's turn) becomes a seat choice for that seat, so
                    // it answers its own reaction instead of freezing the turn. Runs FIRST so the
                    // escape opener below (which waits on any pending choice) sees the reaction as an
                    // open seat choice and holds until it resolves. Early no-op when the queue is empty
                    // or its front belongs to the active player (who keeps the legacy move).
                    openNonActiveReturnOnDiscardSeatChoiceIfNeeded(G, events, ctx?.currentPlayer);
                    // why: WP-793 / D-24656 — open the rulebook HQ KO + Bystander discard every escape
                    // this move recorded (rules v23 L556–L570; steps 1–2 resolve after the move, the
                    // D-24656 point 4 deviation). Runs here, after every move, so EVERY escape path in
                    // every stage is covered and each resolveSeatChoice that resolves a step opens the
                    // next. Runs BEFORE the Diving Block opener: an escape's steps are owed in rule
                    // order and no escape adds a Wound any more, so a Diving Block wave from a step-3
                    // Escape Wound waits behind them. ctx.currentPlayer is the escape's chooser and the
                    // seat the D-24648 stage-ride skip applies to. Early no-op when nothing is queued.
                    // why: WP-829 / D-24762 — Go Back in Time: KO every HQ Hero sharing a Purged Hero Name
                    // (a recruit or KO refill this move may have drawn one) BEFORE any opener lists the HQ,
                    // so no HQ pick (escape or Purge) offers a Hero the rule is about to KO. No-op until
                    // the scheme has Purged something.
                    sweepPurgedHeroesFromHq(G);
                    openEscapeProcedureSeatChoiceIfNeeded(G, events, ctx?.currentPlayer);
                    // why: WP-829 / D-24762 — open the next owed Purge (the active player's HQ pick; it
                    // sweeps again itself before listing). Then sweep once more for an escape auto-KO's
                    // refill, but only while no seat choice is parked: a sweep under an open HQ pick
                    // would KO one of its options. A parked choice blocks every move, and the sweep at the
                    // top of the next onMove catches what was left. The re-runs just below catch a
                    // refill that emptied the Hero Deck.
                    openTimestreamPurgeSeatChoiceIfNeeded(G, events, ctx?.currentPlayer);
                    if (G.pendingSeatChoice === undefined) {
                        sweepPurgedHeroesFromHq(G);
                    }
                    // why: WP-793 / D-24656 — both checks above ran BEFORE the openers, so an automatic
                    // escape KO whose HQ refill emptied the Hero Deck would only be caught on the NEXT
                    // move. Re-run them (both idempotent) so the deck-out latch and Super Hero Civil
                    // War's loss land on this move.
                    latchFinalTurnIfDeckExhausted(G);
                    applyPileDepletionResourceLoss(G);
                    // why: WP-845 / D-24779 — open the next owed reveal-or-gain-a-Wound choice for the
                    // players who could reveal (rules v23 L3196-3200). AFTER the escape and Purge openers
                    // and the re-check above, so an Escape-timing entry resolves after that escape's steps
                    // 1-2 (the D-24750 order) and evaluateEndgame already sees a scheme loss; BEFORE the
                    // Diving Block opener, so a Diving Block wave from an inline cannot-reveal Wound waits
                    // until the revealers answer. An empty Wound supply resolves the entry as all revealing
                    // with no prompt (declining is not an option then, L3199-3200). Early no-op when
                    // nothing is queued.
                    openRevealOrWoundSeatChoiceIfNeeded(G, events, ctx?.currentPlayer);
                    // why: WP-682 / D-24499 — after every move, open (or re-open) the next
                    // Diving-Block reveal/decline WAVE for any Wounds parked at the
                    // gainWoundForPlayer chokepoint this move. Runs here (not in each
                    // wound-causing move) so EVERY wound path is covered and each resolveSeatChoice
                    // that drains a wave triggers the next one. Uses events.setActivePlayers (the
                    // WP-684 stage-ride) to admit a NON-active wound recipient. Early no-op when no
                    // Diving-Block Wound is pending or another seat choice is already open, so a
                    // normal game does no work here and stays byte-identical.
                    // why: D-24648 — pass ctx.currentPlayer so a Diving-Block wave addressing the
                    // active player's own Wound does NOT stage-ride them (they already accept moves
                    // as the currentPlayer). The removed open→resolve→revert frame is what froze a
                    // start-stage escape-wound Diving Block until reload.
                    // why: ctx?. — some unit tests invoke this onMove with only { G } (no ctx);
                    // an absent ctx yields currentPlayer=undefined, which admits every addressed
                    // seat exactly as before (the pre-D-24648 behaviour), never a throw.
                    openDivingBlockSeatChoiceIfNeeded(G, events, ctx?.currentPlayer);
                    // why: WP-827 / D-24760 — then the Flying Shield Block wave (any seat may reveal);
                    // it waits while a Diving Block Wound is pending, since a decline there can park one.
                    openFlyingShieldBlockSeatChoiceIfNeeded(G, events, ctx?.currentPlayer);
                    // why: WP-828 / D-24761 — resolve owed Man Out of Time grants (Winter Soldier's 2>4)
                    // after the move that played the card: auto for one eligible hand Hero, else the
                    // active player's seat choice. No-op while another seat choice is open.
                    openOutOfTimeGrantSeatChoiceIfNeeded(G, events, ctx?.currentPlayer);
                    // why: WP-830 / D-24763 — The Unbreakable Enigma Code: open the next owed pay-1-Recruit
                    // peek offer from a Villain fight (the fighter's own choice). LAST, so every reaction the
                    // fight caused (an escape's steps, a Diving Block wave) resolves first: the peek is
                    // decided once the fight has fully resolved. No-op until the scheme queues one.
                    openEnigmaPeekSeatChoiceIfNeeded(G, events, ctx?.currentPlayer);
                },
                // why: Each new turn must begin at the first canonical turn stage.
                // TURN_STAGES[0] is used instead of a hardcoded string to prevent
                // drift if stage names ever change in turnPhases.types.ts.
                onBegin: ({ G, ctx, random }) => {
                    // why: TURN_STAGES is a readonly array with known contents. The
                    // non-null assertion is safe because TURN_STAGES always has at
                    // least one element (enforced by drift-detection tests in WP-007A).
                    G.currentStage = TURN_STAGES[0];
                    // why: WP-568 / D-24377 — the wait-and-see window is THIS turn. A threshold
                    // never reached during the turn never fires, and an entry surviving into the
                    // next turn would re-evaluate against a fresh G.turnEconomy and could grant
                    // for the wrong turn. Cleared here, beside the other per-turn resets.
                    clearDeferredConditionalGrants(G);
                    // why: WP-656 / D-24467 — defensive turn-boundary clear of the defeat edge
                    // signal. resolveDeferredHeroGrants consumes it after every move, so it is
                    // normally already absent by the turn boundary; this guarded delete makes the
                    // per-turn reset explicit beside clearDeferredConditionalGrants and never
                    // carries a stale defeat into the next turn. Guarded so a game that never
                    // set it is byte-unchanged (a no-op when unset).
                    if (G.villainOrMastermindDefeatedSinceResolve !== undefined) {
                        delete G.villainOrMastermindDefeatedSinceResolve;
                    }
                    // why: WP-853 / D-24789 — the same defensive turn-boundary clear for the
                    // owned-KO count (Absorb Energies). The resolve consumes it every move; this
                    // makes the reset explicit. Guarded, so a game that never set it is unchanged.
                    if (G.ownedCardsKoedSinceResolve !== undefined) {
                        delete G.ownedCardsKoedSinceResolve;
                    }
                    // why: WP-856 / D-24792 — the same defensive turn-boundary clear for the
                    // per-owner Hero-KO count (Possessed by Brother's Spirit). Guarded, so a game
                    // that never set it is unchanged.
                    if (G.ownedHeroesKoedSinceResolve !== undefined) {
                        delete G.ownedHeroesKoedSinceResolve;
                    }
                    // why: WP-855 / D-24791 — the same turn-boundary clear for the per-owner deck-KO
                    // count (Endless Appetite). The resolve keeps it while a choice is parked, so this is
                    // the clear that drops a count held to the end of the turn. Guarded, so a game that
                    // never set it is unchanged.
                    if (G.deckCardsKoedSinceResolve !== undefined) {
                        delete G.deckCardsKoedSinceResolve;
                    }
                    // why: WP-859 / D-24795 — the turn record of cards KO'd from each deck ("KO'd from
                    // your deck this turn") lives for the whole turn and is cleared only here, never per
                    // resolve. Guarded, so a game that never wrote it (no reader hook) is unchanged.
                    if (G.cardsKoedFromDeckThisTurn !== undefined) {
                        delete G.cardsKoedFromDeckThisTurn;
                    }
                    // why: WP-777 / D-24616 — "If you discarded any cards this turn" reads a per-turn
                    // count, so it must not carry into the next turn (a stale count would let New
                    // Wings / Pumpkin Bombs fire with no discard — the exact over-credit this fixes).
                    // Guarded so a match that never wrote the counter stays byte-unchanged.
                    if (G.cardsDiscardedThisTurn !== undefined) {
                        delete G.cardsDiscardedThisTurn;
                    }
                    // why: WP-743 / D-24566 — the Master Strike / Ambush Villain flags are THIS
                    // turn only (Grief / Spring the Trap). Guarded deletes, so a game that never set
                    // them is byte-unchanged; applyOnBeginParity mirrors these for the rebuilt loops.
                    if (G.masterStrikePlayedThisTurn !== undefined) {
                        delete G.masterStrikePlayedThisTurn;
                    }
                    if (G.ambushVillainPlayedThisTurn !== undefined) {
                        delete G.ambushVillainPlayedThisTurn;
                    }
                    // why: WP-827 / D-24760 — Flying Shield Block's "once per turn" resets each turn.
                    // Guarded delete, so a match that never revealed one is byte-unchanged;
                    // applyOnBeginParity mirrors it for the rebuilt loops.
                    if (G.flyingShieldBlockUsedThisTurn !== undefined) {
                        delete G.flyingShieldBlockUsedThisTurn;
                    }
                    // why: WP-828 / D-24761 — a Man Out of Time grant lasts this turn only. Guarded deletes,
                    // so a match that never granted one is byte-unchanged; applyOnBeginParity mirrors them.
                    if (G.outOfTimeGrantedThisTurn !== undefined) {
                        delete G.outOfTimeGrantedThisTurn;
                    }
                    if (G.pendingOutOfTimeGrants !== undefined) {
                        delete G.pendingOutOfTimeGrants;
                    }
                    // why: WP-328 — stamp the turn number into G (ctx.turn lives only in ctx, and
                    // helper push sites have no ctx) and reset the per-step action counter, so
                    // pushLog can number every log line {turn}.{step}.{action}. NOTE (WP-337):
                    // `computeStateHash` (replay.hash.ts) INTENTIONALLY hashes the full G, so
                    // logMeta IS part of the competitive/determinism hash — that is correct and
                    // harmless (logMeta is deterministic, and the faithful reduction reproduces it
                    // exactly, D-24124). It is the SEPARATE fixture-golden hash (hashGameState) that
                    // excludes messages + logMeta for golden-churn reasons (D-24081 / D-24114), not
                    // this one. logMeta is deterministic (ctx.turn), so it adds no non-determinism.
                    //
                    // why: the displayed turn number must be PLAY-RELATIVE (first play turn = 1).
                    // The lobby phase consumes framework turn 1 (startMatchIfReady exits via
                    // setPhase('play')), so the play phase's first turn arrives as ctx.turn === 2.
                    // Without this offset the game log opened at "2.1.1" and the HUD header read
                    // "Turn 2" on the very first turn. Capture the framework turn of the first play
                    // turn once (firstPlayTurn) and offset every turn by it, so the numbering is
                    // correct regardless of how many framework turns preceded play. firstPlayTurn is
                    // carried across the per-turn logMeta reassignment.
                    const firstPlayTurn = G.logMeta?.firstPlayTurn ?? ctx.turn;
                    G.logMeta = {
                        turn: ctx.turn - firstPlayTurn + 1,
                        actionInStep: 0,
                        firstPlayTurn,
                    };
                    // why: economy resets at start of each player turn — accumulated
                    // and spent values from previous turn are cleared
                    G.turnEconomy = resetTurnEconomy();
                    // why: the once-per-turn villain reveal allowance refreshes at the
                    // start of every player turn; without this reset the wrapper guard
                    // would permanently block reveals from turn 2 onward.
                    G.villainRevealedThisTurn = false;
                    // why: WP-701 / D-24520 — the start-of-turn hand is ALREADY drawn (at the
                    // end of the previous turn, or at setup for turn 1), so the scaffold
                    // `drawCards` move must stay a guarded no-op ALL turn — a mid-turn manual
                    // refill is not a legal action under the end-of-turn-draw model. Set the
                    // flag TRUE (matching the OLD model's post-onBegin-draw value), so the
                    // move guard + ai.legalMoves both treat this turn's draw as already spent.
                    G.hasDrawnThisTurn = true;
                    // why: WP-379 / D-24180 — the Healing/act mutual-exclusion allowance
                    // refreshes at the start of every player turn. Without this reset a
                    // player who acted or healed last turn would stay locked out this turn.
                    G.hasActedThisTurn = false;
                    G.hasHealedThisTurn = false;
                    // why: WP-409 / D-24221 — the per-turn hero-effect-fired count is a
                    // transient observability signal; a fresh turn starts at 0 before any
                    // play (overwritten each play by applyCardPlay). Deliberately NOT seeded
                    // in buildInitialGameState (unlike hasActedThisTurn etc.), so the empty
                    // PRE_WP080 replay never sets it and that whole-G oracle is byte-unchanged;
                    // it is excluded from the finalStateHash oracle, so sentinels hold too —
                    // no hash re-pin (verified: full engine suite green).
                    G.lastPlayEffectsFired = 0;
                    // why: WP-701 / D-24520 — the start-of-turn auto-draw is RETIRED. The new hand
                    // is now drawn at the END of the previous turn (applyEndOfTurnCleanup, reached
                    // from both D-22002 turn-end paths) and initial hands are dealt at setup, so a
                    // player holds a full hand during opponents' turns and onBegin only resets
                    // per-turn flags. The incoming player already holds their hand; the
                    // handSizeOverrides / deferredHandInjections consume travels to the end-of-turn
                    // cleanup. hasDrawnThisTurn stays reset to false above (it guards the scaffold
                    // drawCards move, which now computes a 0-card no-op against the full hand).
                    // why: trigger -> collect effects -> apply effects pipeline.
                    // onTurnStart fires at the beginning of each player's turn so
                    // scheme hooks can react to the new turn.
                    const turnStartEffects = executeRuleHooks(G, ctx, 'onTurnStart', { currentPlayerId: ctx.currentPlayer }, G.hookRegistry, DEFAULT_IMPLEMENTATION_MAP);
                    applyRuleEffects(G, ctx, turnStartEffects);
                },
                onEnd: ({ G, ctx }) => {
                    // why: trigger -> collect effects -> apply effects pipeline.
                    // onTurnEnd fires at the end of each player's turn so mastermind
                    // hooks can react to the turn ending.
                    const turnEndEffects = executeRuleHooks(G, ctx, 'onTurnEnd', { currentPlayerId: ctx.currentPlayer }, G.hookRegistry, DEFAULT_IMPLEMENTATION_MAP);
                    applyRuleEffects(G, ctx, turnEndEffects);
                    // why: WP-732 / D-24553 — promote a pending Mastermind victory to the
                    // terminal win at the end of the winning player's turn (the real
                    // end-of-game moment for the deferred win). Runs AFTER the onTurnEnd rule
                    // pipeline and BEFORE resolveFinalTurnTieIfUnresolved so the now-terminal
                    // MASTERMIND_DEFEATED makes evaluateEndgame !== null and the
                    // deck-exhaustion tie correctly skips (its evaluateEndgame === null
                    // guard). Terminal is order-independent vs SCHEME_LOSS only because
                    // evaluateEndgame checks the terminal Mastermind win before SCHEME_LOSS.
                    promoteMastermindVictoryIfPending(G);
                    // why: WP-367 / D-24159 — resolve the deck-exhaustion tie at the end of
                    // the final turn. Runs AFTER the onTurnEnd effects above so an
                    // end-of-turn effect that itself triggers a win/loss is honored before
                    // the tie is considered. The helper only sets the tie when the final
                    // turn is latched and no win/loss fired — see finalTurn.logic.ts.
                    resolveFinalTurnTieIfUnresolved(G);
                },
            },
        },
        end: {},
    },
};
//# sourceMappingURL=game.js.map